import { AnalyticIdentifier } from '@common/neo.tax';
import { Button, colors, ControlledInput, ModalActions, ModalHeader, Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as Presentation } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useCreateContributor, type Contributor } from '@neo.tax/asc/hooks/useCreateContributor';
import { useAnalyticId, useCurrentCompanyId } from '@neo.tax/shared';

import { Modal } from './contributor-creation-modal.styled';

type ContributorCreationForm = {
  name: string;
};

type ContributorCreationModalProps = {
  initialName?: string | null;
  isOpen: boolean;
  onClose: (newContributor?: Contributor) => void;
};

export const ContributorCreationModal = ({
  initialName,
  isOpen,
  onClose,
}: ContributorCreationModalProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  const [createContributor, { loading }] = useCreateContributor();
  const { control, handleSubmit, reset } = useForm<ContributorCreationForm>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  });

  const nameId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_CREATE_CONTRIBUTOR_NAME);

  const handleCancel = (): void => {
    reset({
      name: '',
    });
    onClose();
  };

  const onSubmit: SubmitHandler<ContributorCreationForm> = async (data) => {
    const result = await createContributor({
      variables: {
        companyId,
        input: data,
      },
    });
    onClose(result.data?.createContributor);
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        name: initialName ?? '',
      });
    }
  }, [initialName, isOpen, reset]);

  return (
    <Modal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={2} onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          icon={<Presentation width={24} color={colors.blue[600]} />}
          iconBgColor={colors.blue[50]}
          title={
            <Typography fontSize="xl" fontWeight="medium">
              Create New Team Member
            </Typography>
          }
        />

        <Stack direction="column">
          <ControlledInput
            control={control}
            id={nameId}
            name="name"
            label="Team Member Name"
            rules={{ required: 'Please provide a name' }}
          />
        </Stack>

        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit" disabled={loading}>
            Create Team Member
          </Button>
        </ModalActions>
      </Stack>
    </Modal>
  );
};
